import React, { useState, useEffect } from 'react';
import { getSubjectChapterPerformance } from '../apiClient';

interface ChapterData {
  chapter_id: number;
  chapter_name: string;
  total_questions_attempted: number;
  questions_answered: number;
  correct_answers: number;
  incorrect_answers: number;
  unanswered_questions: number;
  accuracy_percentage: number;
  total_time_spent_seconds: number;
  average_time_per_question: number;
  best_quiz_score: number;
  attempts_count: number;
  last_attempt_date: string;
  chapter_rank: number;
}

interface SubjectData {
  subject_id: number;
  subject_name: string;
  chapters: ChapterData[];
  subject_summary: {
    total_chapters_attempted: number;
    total_questions_attempted: number;
    overall_accuracy_percentage: number;
    strongest_chapter: string;
    weakest_chapter: string;
  };
}

interface ChapterPerformanceData {
  user_id: number;
  overall_stats: {
    total_subjects_attempted: number;
    total_chapters_attempted: number;
    total_questions_attempted: number;
    overall_accuracy_percentage: number;
  };
  subjects: SubjectData[];
}

const ChapterPerformance: React.FC = () => {
  const [chapterData, setChapterData] = useState<ChapterPerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSubjects, setExpandedSubjects] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadChapterPerformanceData();
  }, []);

  const loadChapterPerformanceData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getSubjectChapterPerformance();
      if (response?.result) {
        setChapterData(response.result);
        // Start with all subjects collapsed
        setExpandedSubjects(new Set<number>());
      } else {
        setError('No chapter performance data found');
      }
    } catch (error: any) {
      console.error('Failed to load chapter performance data:', error);
      setError(error.message || 'Failed to load chapter performance data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    if (seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPerformanceColor = (percentage: number): string => {
    if (percentage >= 70) return 'success';
    if (percentage >= 50) return 'warning';
    if (percentage >= 30) return 'info';
    return 'danger';
  };

  const getRankBadgeColor = (rank: number): string => {
    if (rank === 1) return 'success';
    if (rank <= 3) return 'primary';
    if (rank <= 5) return 'info';
    return 'secondary';
  };

  const toggleSubjectExpansion = (subjectId: number) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
    }
    setExpandedSubjects(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <h5 className="card-title mb-4">
            <i className="fas fa-book-open me-2 text-primary"></i>
            Chapter Performance Analysis
          </h5>
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading chapter performance data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <h5 className="card-title mb-4">
            <i className="fas fa-book-open me-2 text-primary"></i>
            Chapter Performance Analysis
          </h5>
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
          <button 
            className="btn btn-primary" 
            onClick={loadChapterPerformanceData}
          >
            <i className="fas fa-refresh me-2"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!chapterData || chapterData.subjects.length === 0) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <h5 className="card-title mb-4">
            <i className="fas fa-book-open me-2 text-primary"></i>
            Chapter Performance Analysis
          </h5>
          <div className="text-center py-5">
            <i className="fas fa-book fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No chapter data available</h6>
            <p className="text-muted small">Complete some quizzes to see your chapter-wise performance here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
      <div className="card-body p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="card-title mb-0">
            <i className="fas fa-book-open me-2 text-primary"></i>
            Chapter Performance Analysis
          </h5>
        </div>

        {/* Overall Statistics */}
        <div className="row mb-4">
          <div className="col-md-3 text-center">
            <div className="bg-light p-3 rounded">
              <h6 className="text-muted mb-1">Subjects</h6>
              <strong className="text-primary">{chapterData.overall_stats.total_subjects_attempted}</strong>
            </div>
          </div>
          <div className="col-md-3 text-center">
            <div className="bg-light p-3 rounded">
              <h6 className="text-muted mb-1">Chapters</h6>
              <strong className="text-info">{chapterData.overall_stats.total_chapters_attempted}</strong>
            </div>
          </div>
          <div className="col-md-3 text-center">
            <div className="bg-light p-3 rounded">
              <h6 className="text-muted mb-1">Questions</h6>
              <strong className="text-warning">{chapterData.overall_stats.total_questions_attempted}</strong>
            </div>
          </div>
          <div className="col-md-3 text-center">
            <div className="bg-light p-3 rounded">
              <h6 className="text-muted mb-1">Overall Accuracy</h6>
              <strong className={`text-${getPerformanceColor(chapterData.overall_stats.overall_accuracy_percentage)}`}>
                {chapterData.overall_stats.overall_accuracy_percentage.toFixed(1)}%
              </strong>
            </div>
          </div>
        </div>

        {/* Subject-wise Chapter Performance */}
        <div className="accordion" id="chapterAccordion">
          {chapterData.subjects.map((subject, subjectIndex) => (
            <div key={subject.subject_id} className="accordion-item border-0 mb-3" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              {/* Subject Header */}
              <div className="accordion-header">
                <button
                  className={`accordion-button ${expandedSubjects.has(subject.subject_id) ? '' : 'collapsed'}`}
                  type="button"
                  onClick={() => toggleSubjectExpansion(subject.subject_id)}
                  style={{ 
                    borderRadius: expandedSubjects.has(subject.subject_id) ? '12px 12px 0 0' : '12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div>
                      <h6 className="mb-1 fw-bold">{subject.subject_name}</h6>
                      <small className="text-muted">
                        {subject.subject_summary.total_chapters_attempted} chapters • 
                        {subject.subject_summary.total_questions_attempted} questions • 
                        {subject.subject_summary.overall_accuracy_percentage.toFixed(1)}% accuracy
                      </small>
                    </div>
                    <div className="text-end">
                      <span className={`badge bg-${getPerformanceColor(subject.subject_summary.overall_accuracy_percentage)}`}>
                        {subject.subject_summary.overall_accuracy_percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </button>
              </div>

              {/* Subject Content */}
              <div 
                className={`accordion-collapse collapse ${expandedSubjects.has(subject.subject_id) ? 'show' : ''}`}
              >
                <div className="accordion-body pt-2">
                  {/* Subject Summary */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <small className="text-success">
                        <i className="fas fa-trophy me-1"></i>
                        <strong>Strongest:</strong> {subject.subject_summary.strongest_chapter}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <small className="text-danger">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        <strong>Needs Focus:</strong> {subject.subject_summary.weakest_chapter}
                      </small>
                    </div>
                  </div>

                  {/* Chapters Table */}
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Rank</th>
                          <th>Chapter</th>
                          <th className="text-center">Questions</th>
                          <th className="text-center">Correct</th>
                          <th className="text-center">Accuracy</th>
                          <th className="text-center">Time</th>
                          <th className="text-center">Attempts</th>
                          <th className="text-center">Last Attempt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subject.chapters.map((chapter) => (
                          <tr key={chapter.chapter_id}>
                            {/* Rank */}
                            <td>
                              <span className={`badge bg-${getRankBadgeColor(chapter.chapter_rank)}`}>
                                #{chapter.chapter_rank}
                              </span>
                            </td>

                            {/* Chapter Name */}
                            <td>
                              <div>
                                <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.9rem' }}>
                                  {chapter.chapter_name.length > 25 
                                    ? chapter.chapter_name.substring(0, 25) + '...' 
                                    : chapter.chapter_name}
                                </h6>
                                <small className="text-muted">
                                  {chapter.questions_answered}/{chapter.total_questions_attempted} answered
                                </small>
                              </div>
                            </td>

                            {/* Questions */}
                            <td className="text-center">
                              <strong>{chapter.total_questions_attempted}</strong>
                            </td>

                            {/* Correct */}
                            <td className="text-center">
                              <span className="text-success fw-bold">{chapter.correct_answers}</span>
                              <br />
                              <small className="text-danger">{chapter.incorrect_answers} wrong</small>
                            </td>

                            {/* Accuracy */}
                            <td className="text-center">
                              <span className={`badge bg-${getPerformanceColor(chapter.accuracy_percentage)}`}>
                                {chapter.accuracy_percentage.toFixed(1)}%
                              </span>
                              <br />
                              <div className="progress mt-1" style={{ height: '3px', width: '50px' }}>
                                <div 
                                  className={`progress-bar bg-${getPerformanceColor(chapter.accuracy_percentage)}`}
                                  style={{ width: `${chapter.accuracy_percentage}%` }}
                                ></div>
                              </div>
                            </td>

                            {/* Time */}
                            <td className="text-center">
                              <strong>{formatTime(chapter.total_time_spent_seconds)}</strong>
                              <br />
                              <small className="text-muted">
                                {chapter.average_time_per_question.toFixed(1)}s avg
                              </small>
                            </td>

                            {/* Attempts */}
                            <td className="text-center">
                              <span className="badge bg-info">{chapter.attempts_count}</span>
                              <br />
                              <small className="text-muted">
                                Best: {chapter.best_quiz_score}
                              </small>
                            </td>

                            {/* Last Attempt */}
                            <td className="text-center">
                              <small className="text-muted">
                                {formatDate(chapter.last_attempt_date)}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="text-center mt-4 pt-3 border-top">
          <small className="text-muted">
            <i className="fas fa-info-circle me-1"></i>
            Chapters are ranked by accuracy within each subject. Focus on lower-ranked chapters for improvement.
          </small>
        </div>
      </div>
    </div>
  );
};

export default ChapterPerformance;
